/**
 * SafeRunnerStep.jsx
 * 체험형 레슨 스텝: 안전장치 포함 코드 실행 시뮬레이션
 *
 * "RUN" 버튼 클릭 시 클라이언트 사이드에서 루프를 시뮬레이션합니다.
 * break 키워드 존재 여부와 safety_limits로 Circuit Breaker를 구현합니다.
 * 실제 코드 실행이 아닌 시뮬레이션입니다.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import MonacoCodeEditor from '@components/editor/MonacoCodeEditor';
import ControlBar from '../ControlBar';
import { motion } from 'framer-motion';

function safeRegexExtract(code, regex, defaultVal) {
  try {
    const match = code.match(new RegExp(regex));
    return match ? match[1] : defaultVal;
  } catch {
    return defaultVal;
  }
}

function SafeRunnerStep({ step, onBack, onNext, canGoBack }) {
  const [code, setCode] = useState(step.initialCode || '# 코드를 수정해보세요');
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const logContainerRef = useRef(null);
  const language = step.language || 'python';
  const safetyLimits = step.safety_limits || { max_iterations: 100 };

  const extraction = step.extraction || {};
  const extractedLimit = useMemo(
    () => parseInt(safeRegexExtract(code, extraction.regex || '(\\d+)', '5')) || 5,
    [code, extraction.regex]
  );

  const hasBreak = useMemo(() => code.includes('break'), [code]);
  const hasWhileTrue = useMemo(
    () => code.includes('while True') || code.includes('while true'),
    [code]
  );

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, error]);

  const runSimulation = useCallback(() => {
    setLogs([]);
    setError(null);
    setIsRunning(true);

    const newLogs = [];
    newLogs.push('데이터 수집 시작...');

    let circuitBroken = false;
    const maxIter = safetyLimits.max_iterations || 100;

    if (hasWhileTrue) {
      let count = 0;
      while (true) {
        count++;
        newLogs.push(`[${count}회차] 데이터 수집 완료`);

        // User's break logic
        if (hasBreak && count >= extractedLimit) {
          newLogs.push('수집 종료');
          break;
        }

        // Safety guard
        if (count > maxIter) {
          circuitBroken = true;
          break;
        }
      }
    } else {
      // Simple for-loop simulation
      const iterations = Math.min(extractedLimit, maxIter);
      for (let i = 1; i <= iterations; i++) {
        newLogs.push(`[${i}회차] 실행 완료`);
      }
      if (extractedLimit > maxIter) {
        circuitBroken = true;
      } else {
        newLogs.push('실행 종료');
      }
    }

    // Animate logs one by one
    let i = 0;
    const interval = setInterval(() => {
      if (i < newLogs.length) {
        setLogs(prev => [...prev, newLogs[i]]);
        i++;
      } else {
        clearInterval(interval);
        if (circuitBroken) {
          setError('Circuit Breaker: 무한 루프 감지! (안전장치 동작)');
        }
        setIsRunning(false);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [hasWhileTrue, hasBreak, extractedLimit, safetyLimits.max_iterations]);

  const runButton = (
    <motion.button
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      onClick={runSimulation}
      disabled={isRunning}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
        isRunning
          ? 'bg-slate-700 text-slate-400 cursor-wait'
          : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
      }`}
    >
      {isRunning ? (
        <><i className="fas fa-spinner fa-spin text-[10px]" /> Running...</>
      ) : (
        <><i className="fas fa-play text-[10px]" /> RUN SAFE</>
      )}
    </motion.button>
  );

  return (
    <div className="relative h-full flex flex-col">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] font-bold rounded uppercase tracking-wider">
            Safety Guard
          </span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1">{step.title || '안전 실행기'}</h3>
        {step.text && <p className="text-sm text-slate-400 leading-relaxed">{step.text}</p>}
      </div>

      {/* Split View: Editor + Safety Monitor */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Code Editor */}
        <div className="w-1/2 flex flex-col border-r border-slate-800 min-h-0">
          <div className="px-4 py-2 bg-slate-900/50 text-xs font-mono text-slate-500 flex items-center justify-between border-b border-slate-800/50">
            <div className="flex items-center gap-2">
              <i className="fas fa-code text-[10px]" /> main.py
            </div>
            {runButton}
          </div>
          <div className="flex-1 min-h-0">
            <MonacoCodeEditor
              initialCode={step.initialCode || ''}
              language={language}
              onChange={setCode}
              height="100%"
              className="h-full flex flex-col"
              hideHeader={true}
            />
          </div>
        </div>

        {/* Right: Safety Monitor */}
        <div className="w-1/2 bg-[#1e1e1e] flex flex-col min-h-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 text-xs font-mono text-slate-500">
            <div className="flex items-center gap-2">
              <i className="fas fa-shield-halved text-emerald-500 text-[10px]" />
              SAFETY MONITOR
            </div>
            <span className="text-slate-600">limit: {safetyLimits.max_iterations}</span>
          </div>
          <div ref={logContainerRef} className="flex-1 bg-black p-4 font-mono text-sm overflow-y-auto">
            {logs.length === 0 && !error && (
              <div className="text-slate-600 italic">Ready to run...</div>
            )}
            {logs.map((log, i) => (
              <motion.div
                key={`${i}-${log}`}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-slate-300 mb-1 border-l-2 border-slate-800 pl-2"
              >
                {log}
              </motion.div>
            ))}
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-3 p-3 bg-red-500/20 text-red-400 border border-red-500/50 rounded font-bold"
              >
                <i className="fas fa-ban mr-2" />{error}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <ControlBar onBack={onBack} onNext={onNext} canGoBack={canGoBack} />
    </div>
  );
}

export default SafeRunnerStep;
